/**
 * ErrorBoundary - contain a render/lifecycle crash to one subtree
 *
 * A class component because React 19 still has no hook equivalent and
 * `react-error-boundary` is not a dependency here. Renders `fallback` (default:
 * nothing) instead of propagating, so a crash in a background or non-visual
 * subtree cannot unmount the app.
 */

import React from 'react'

type ErrorBoundaryProps = {
  children: React.ReactNode
  /** Rendered in place of the subtree after a crash. Defaults to nothing. */
  fallback?: React.ReactNode
  /** Names the subtree in the console report. */
  label?: string
}

type ErrorBoundaryState = {
  hasError: boolean
}

// @purpose Class error boundary that renders nothing (or an optional `fallback`) when its subtree throws — use it to isolate a background/non-visual subtree such as a polling producer so its crash cannot take the app down
export class ErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  state: ErrorBoundaryState = { hasError: false }

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true }
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    console.error(
      `ErrorBoundary (${this.props.label ?? 'unlabelled'}) caught an error; the subtree was unmounted.`,
      error,
      info.componentStack,
    )
  }

  render(): React.ReactNode {
    if (this.state.hasError) {
      return this.props.fallback ?? null
    }
    return this.props.children
  }
}

export default ErrorBoundary
